import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LandingHeader from "../../Components/Landing/LandingHeader";
import LandingFooter from "../../Components/Landing/LandingFooter";
import CartService from "../../Services/CartService";
import shippingStore from "../../stores/ShippingStore";
import { observer } from "mobx-react-lite";


const validationSchema = Yup.object().shape({
  firstName:       Yup.string().required("First Name is required"),
  lastName:        Yup.string().required("Last Name is required"),
  phoneNo:         Yup.string().matches(/^\d{10}$/, "Phone number must be 10 digits").required("Phone Number is required"),
  shippingAddress: Yup.string().required("Address is required"),
  country:         Yup.string().required("Country is required"),
  city:            Yup.string().required("City is required"),
  state:           Yup.string().required("State is required"),
  zipCode:         Yup.string().matches(/^\d{5}$/, "Postal Code must be 5 digits").required("Postal Code is required"),
});

interface FormShoppingDetails {
  firstName: string; lastName: string; phoneNo: string;
  shippingAddress: string; country: string; state: string;
  city: string; zipCode: string;
}
interface OrderSummary { subTotal: number; discount: number; taxAmount: number; totalAmount: number; deliveryCharges: number; }

const C = {
  page: '#EEEEEE', surface: '#FFFFFF', surface2: '#FFFFFF', surface3: '#F3F7FF', surface4: '#EAF1FF',
  border: '#E5E7EB', divider: '#F1F5F9',
  text1: '#0B1B3F', text2: '#475569', text3: '#64748B',
  accent: '#0B5FFF', accentSoft: '#EAF1FF', accentBorder: 'rgba(11,95,255,0.3)',
  success: '#0B5FFF', successHover: '#0847C7', danger: '#DC2626',
};

const iStyle: React.CSSProperties = {
  display: 'block', width: '100%', boxSizing: 'border-box',
  padding: '9px 12px', marginTop: 6,
  background: C.surface3, border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.text1, fontSize: 13,
  outline: 'none', fontFamily: 'inherit', transition: 'border-color 150ms',
};

const lStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.text2, display: 'block' };

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri",
  "Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York",
  "North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia",
  "Washington","West Virginia","Wisconsin","Wyoming",
];

const ShippingDetails = observer(() => {
  const [orderSummary, setOrderSummary]   = useState<OrderSummary | null>(null);
  const [cartItems, setCartItems]         = useState<any[]>([]);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [submitting, setSubmitting]       = useState(false);

  const location = useLocation();
  const { specialInstructions } = location.state || {};

  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<FormShoppingDetails>({ resolver: yupResolver(validationSchema) });

  useEffect(() => {
    const existing = shippingStore.shippingDetails;
    if (existing) reset({ ...existing });
  }, [reset]);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const { cartItems, subtotal, deliveryCharge, taxAmount, totalAmount } = await CartService.GetCart();
        setCartItems(cartItems);
        setOrderSummary({ subTotal: subtotal, discount: 3, taxAmount, totalAmount, deliveryCharges: deliveryCharge });
      } catch { /* silent */ }
    };
    const fetchCount = async () => {
      try { const { CartCount } = await CartService.GetCartCount(); setCartItemCount(CartCount); } catch { /* silent */ }
    };
    fetchCart(); fetchCount();
  }, []);

  const onSubmit = async (data: FormShoppingDetails) => {
    setSubmitting(true);
    try {
      shippingStore.updateField({
        specialInstructions: specialInstructions || "",
        shippingDetails: { ...data, shippingMethod: "" },
      });
      window.location.replace(process.env.REACT_APP_PublicPath + "/paymentdetails");
    } catch {
      toast.error("Failed to save shipping details", { position: "top-center" });
    } finally { setSubmitting(false); }
  };

  const fo = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.currentTarget.style.borderColor = C.accentBorder);
  const bl = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.currentTarget.style.borderColor = C.border);

  return (
    <div className="flex min-h-screen flex-col font-jakarta text-ink antialiased" style={{ background: C.page }}>
      <LandingHeader cartItemsCount={cartItemCount} />

      {/* Step progress */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 0" }}>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {[
            { step: 1, label: "Your Cart",        done: true,  active: false },
            { step: 2, label: "Shipping Details", done: false, active: true  },
            { step: 3, label: "Payment Details",  done: false, active: false },
          ].map((s, i, arr) => (
            <React.Fragment key={s.step}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: s.done ? C.success : s.active ? C.accent : C.surface3,
                  border: `2px solid ${s.done ? C.success : s.active ? C.accent : C.border}`,
                  color: (s.done || s.active) ? "#fff" : C.text3,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                }}>
                  {s.done ? "✓" : s.step}
                </div>
                <span style={{ fontSize: 11, fontWeight: s.active ? 600 : 400, color: s.active ? C.text1 : s.done ? C.success : C.text3, whiteSpace: "nowrap" }}>
                  {s.label}
                </span>
              </div>
              {i < arr.length - 1 && <div style={{ width: 80, height: 1, background: C.border, margin: "0 8px", marginBottom: 16 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, width: "100%", maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
        {/* Back + title */}
        <div style={{ marginBottom: 28 }}>
          <Link to="/yourcart" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.text3, fontSize: 13, textDecoration: "none", marginBottom: 16 }}
            onMouseEnter={e => (e.currentTarget.style.color = C.text1)}
            onMouseLeave={e => (e.currentTarget.style.color = C.text3)}
          >
            <ChevronLeftIcon style={{ width: 16, height: 16 }} /> Edit Cart
          </Link>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.text1, margin: 0 }}>Shipping Details</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>

            {/* Left: Form */}
            <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Contact */}
              <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" as const, color: C.text3, padding: "11px 16px", borderBottom: `1px solid ${C.divider}` }}>
                  Contact Details
                </div>
                <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={lStyle}>First Name *</label>
                      <input {...register("firstName")} placeholder="First name" style={iStyle} onFocus={fo} onBlur={bl} />
                      {errors.firstName && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.firstName.message}</p>}
                    </div>
                    <div>
                      <label style={lStyle}>Last Name *</label>
                      <input {...register("lastName")} placeholder="Last name" style={iStyle} onFocus={fo} onBlur={bl} />
                      {errors.lastName && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.lastName.message}</p>}
                    </div>
                  </div>
                  <div style={{ maxWidth: "50%" }}>
                    <label style={lStyle}>Phone No. *</label>
                    <input {...register("phoneNo")} type="tel" inputMode="numeric" maxLength={10}
                      // Digits only, max 10 — runs before register's onChange so the form value is already clean
                      onInput={e => { e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "").slice(0, 10); }}
                      placeholder="e.g. 0355551111" style={iStyle} onFocus={fo} onBlur={bl} />
                    {errors.phoneNo && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.phoneNo.message}</p>}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" as const, color: C.text3, padding: "11px 16px", borderBottom: `1px solid ${C.divider}` }}>
                  Delivery Address
                </div>
                <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <label style={lStyle}>Address *</label>
                    <input {...register("shippingAddress")} placeholder="Street Address or P.O. Box" style={iStyle} onFocus={fo} onBlur={bl} />
                    {errors.shippingAddress && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.shippingAddress.message}</p>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={lStyle}>Country *</label>
                      <select {...register("country")} style={{ ...iStyle, appearance: "none" as const }} onFocus={fo} onBlur={bl}>
                        <option value="">Please select</option>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                      </select>
                      {errors.country && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.country.message}</p>}
                    </div>
                    <div>
                      <label style={lStyle}>City *</label>
                      <input {...register("city")} placeholder="Enter your city" style={iStyle} onFocus={fo} onBlur={bl} />
                      {errors.city && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.city.message}</p>}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={lStyle}>State *</label>
                      <select {...register("state")} style={{ ...iStyle, appearance: "none" as const }} onFocus={fo} onBlur={bl}>
                        <option value="">Please select</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {errors.state && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.state.message}</p>}
                    </div>
                    <div>
                      <label style={lStyle}>Postal Code *</label>
                      <input {...register("zipCode")} placeholder="5 digit zip code" style={iStyle} onFocus={fo} onBlur={bl} />
                      {errors.zipCode && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{errors.zipCode.message}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div style={{ width: 320, flexShrink: 0 }}>
              <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", position: "sticky" as const, top: 24 }}>
                <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.divider}`, background: C.surface, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: C.accentSoft, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                      <rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>Order Summary</span>
                </div>
                <div style={{ padding: "16px 18px" }}>
                  {[
                    { label: "Total Items",      value: String(cartItems.length) },
                    { label: "Sub Total",        value: `$${orderSummary?.subTotal?.toFixed(2) ?? "0.00"}` },
                    { label: "Delivery Charges", value: `$${orderSummary?.deliveryCharges?.toFixed(2) ?? "0.00"}` },
                    { label: "Tax",              value: `$${orderSummary?.taxAmount?.toFixed(2) ?? "0.00"}` },
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${C.divider}` }}>
                      <span style={{ fontSize: 13, color: C.text2 }}>{row.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text1 }}>{row.value}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 0 4px" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.text1 }}>Total</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: C.accent, letterSpacing: -0.5 }}>
                      ${orderSummary?.totalAmount?.toFixed(2) ?? "0.00"}
                    </span>
                  </div>
                </div>
                <div style={{ padding: "0 18px 18px" }}>
                  <button type="submit" disabled={submitting} style={{
                    width: "100%", height: 44,
                    background: submitting ? C.surface3 : C.success,
                    border: "none", borderRadius: 999,
                    color: submitting ? C.text3 : "#fff",
                    fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
                    textTransform: "uppercase" as const,
                    cursor: submitting ? "wait" : "pointer",
                    transition: "all 150ms",
                  }}
                    onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = C.successHover; }}
                    onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = C.success; }}
                  >
                    {submitting ? "Saving…" : "Continue Payment"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        select option { background: #fff; color: #0B1B3F; }
        input::placeholder { color: #94A3B8; }
      `}</style>
      <LandingFooter />
    </div>
  );
});

export default ShippingDetails;
